<script>
import Input from "./types/input";
import Select from "./types/select";
import DateTime from "./types/date";
import InputRange from "./types/inputrange";
import Remote from "./types/remote";

// 支持的搜索组件类型，后续增加日期类型
const ColTypes = ["text", "enum", "datetime", "inputrange", "remote"];

export default {
  name: "CsSearch",
  model: {
    prop: "searchParams",
  },
  props: {
    showSearch: {
      type: Boolean,
      required: false,
      default: true,
    },
    columns: {
      type: Array,
      default: () => [],
    },
    searchParams: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },
  computed: {
    _showSearch() {
      if (this.showSearch === false) {
        return false;
      } else {
        // 没有搜索项时不显示搜索按钮
        if (this.columns.length === 0) {
          return false;
        }
        // 搜索项都是自动刷新时不显示搜索按钮
        if (!this.columns.some((item) => !item.refresh)) {
          return false;
        }
        return true;
      }
    },
  },
  data() {
    return {};
  },
  mounted() {
    this.initParams();
  },
  methods: {
    async dealOptions(items, ...params) {
      return new Promise((resolve) => {
        if (Array.isArray(items)) {
          resolve(items);
        } else {
          const res = items(...params);
          if (Array.isArray(res)) {
            resolve(res);
          } else {
            res.then((data) => {
              resolve(data);
            });
          }
        }
      });
    },
    /**
     * 处理下拉类型的value,items长度大于0时默认第一
     * multiple=true的时候设置的array格式
     * valueType为object的时候需要设置value为object
     */
    dealEnumValue(col) {
      if (col.items.length > 0) {
        if (col.multiple) {
          this.searchParams[col.dataIndex] =
            col.valueType === "object" ? [col.items[0]] : [col.items[0].value];
        } else {
          this.searchParams[col.dataIndex] =
            col.valueType === "object" ? col.items[0] : col.items[0].value;
        }
      } else {
        if (col.multiple) {
          this.searchParams[col.dataIndex] = [];
        } else {
          this.searchParams[col.dataIndex] = undefined;
        }
      }
    },
    // 根据type属性获取列的类型，如果不指定，返回默认类型
    getColType(oCol) {
      return ColTypes.includes(oCol.type) ? oCol.type : ColTypes[0];
    },

    async initParams() {
      const searchParams = this.searchParams;
      for (let key in searchParams) {
        delete searchParams[key];
      }
      // 初始化searchParams完成双向绑定，下面的循环存在同步代码，所以这里单独循环处理
      for (let col of this.columns) {
        this.$set(searchParams, col.dataIndex, col.initValue);
      }

      // 处理columns
      for (let col of this.columns) {
        if (col.type === "enum") {
          const items = col.items;
          if (Object.prototype.toString.call(items) === "[object Function]") {
            // 这里还可以优化，存在initValue的时候可以异步调用，后面优化
            col.items = await this.dealOptions(items);
          }
          // 不存在initValue的时候,需要选取第一项为默认值
          if (!Object.prototype.hasOwnProperty.call(col, "initValue")) {
            this.dealEnumValue(col);
          }
          if (col.onChange) {
            let linkedCols = this.columns.filter(
              (item) => !!col.onChange[item.dataIndex]
            );
            for (let linkedCol of linkedCols) {
              linkedCol.items = await this.dealOptions(
                col.onChange[linkedCol.dataIndex],
                this.searchParams[col.dataIndex],
                linkedCol
              );
              // 不存在initValue的时候,需要选取第一项为默认值
              if (
                !Object.prototype.hasOwnProperty.call(linkedCol, "initValue")
              ) {
                this.dealEnumValue(linkedCol);
              }
            }
          }
        }
      }
      this.onSearch();
    },

    // 搜索按钮的点击处理
    onSearch() {
      this.$refs.form.validate((flag) => {
        if (flag) {
          this.$emit("search", this.searchParams);
        }
      });
    },

    // 用户输入的搜索条件有变化时的处理。如果不是实现搜索，该方法可以为空
    async onSearchChange(oCol, val) {
      // 如果有onChange, 会通知各依赖的列进行处理
      if (oCol.onChange) {
        let linkedCols = this.columns.filter(
          (item) => !!oCol.onChange[item.dataIndex]
        );
        for (let item of linkedCols) {
          item.items = await this.dealOptions(
            oCol.onChange[item.dataIndex],
            this.searchParams[oCol.dataIndex],
            item
          );
          this.dealEnumValue(item);
        }
      }

      if (oCol.onSelect) {
        oCol.onSelect(val);
      }

      // refresh=true时输入值变化后需要刷新列表
      if (oCol.refresh) {
        this.onSearch();
      }
    },
    renderFormItem(col) {
      const type = this.getColType(col);
      if (type === "text") {
        return (
          <Input
            v-model={this.searchParams[col.dataIndex]}
            placeholder={col.placeholder}
            clearable={col.clearable}
            vOn:change={(val) => this.onSearchChange(col, val)}
            vOn:keyup_enter_native={this.onSearch}
          />
        );
      } else if (type === "enum") {
        return (
          <Select
            v-model={this.searchParams[col.dataIndex]}
            filterable={col.filterable}
            value-type={col.valueType}
            clearable={col.clearable}
            placeholder={col.placeholder}
            multiple={col.multiple}
            items={col.items}
            vOn:change={(val) => this.onSearchChange(col, val)}
          />
        );
      } else if (type === "datetime") {
        return (
          <DateTime
            v-model={this.searchParams[col.dataIndex]}
            format={col.format}
            clearable={col.clearable}
            shortcuts={col.shortcuts}
            vOn:change={(val) => this.onSearchChange(col, val)}
          />
        );
      } else if (type === "inputrange") {
        return (
          <InputRange
            v-model={this.searchParams[col.dataIndex]}
            separator={col.separator}
            clearable={col.clearable}
            suffix={col.suffix}
            vOn:change={(val) => this.onSearchChange(col, val)}
            vOn:keyup_enter_native={this.onSearch}
          />
        );
      } else if (type === "remote") {
        return (
          <Remote
            v-model={this.searchParams[col.dataIndex]}
            placeholder={col.placeholder}
            multiple={col.multiple}
            clearable={col.clearable}
            remoteMethod={col.remoteMethod}
            vOn:change={(val) => this.onSearchChange(col, val)}
          />
        );
      }
    },
  },
  render() {
    return (
      <el-form
        ref="form"
        nativeOn={{
          submit: (e) => e.preventDefault(),
        }}
        props={{ model: this.searchParams }}
        inline={true}
      >
        {this.columns.map((col) => {
          return (
            <el-form-item
              rules={col.validate}
              key={col.dataIndex}
              label={col.label}
              prop={col.dataIndex}
            >
              {this.renderFormItem(col)}
            </el-form-item>
          );
        })}
        {this._showSearch && (
          <el-form-item>
            <el-button
              type="primary"
              onClick={this.onSearch}
              icon="el-icon-search"
            >
              搜索
            </el-button>
          </el-form-item>
        )}
      </el-form>
    );
  },
};
</script>
